import {
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  memo,
  useCallback,
  useId,
  useRef,
  useState,
} from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { XIcon } from '@air/design-icons';
import { Chip } from '@air/primitive-chip';
import { IconButton } from '@air/primitive-icon-button';
import { Input } from '@air/primitive-input';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@air/primitive-select';

import { type PlainTextCustomFieldOperator } from '~/store/filters/types';
import {
  isPlainTextCustomFieldOperator,
  PLAIN_TEXT_CUSTOM_FIELD_OPERATOR_LABELS,
  PLAIN_TEXT_CUSTOM_FIELD_OPERATORS,
  type PlainTextCustomFieldValueError,
  plainTextCustomFieldValueErrorMessage,
} from '~/utils/filters/plainTextCustomFieldFilter';

export const PLAIN_TEXT_CUSTOM_FIELD_FILTER_OPERATOR = 'PLAIN_TEXT_CUSTOM_FIELD_FILTER_OPERATOR';
export const PLAIN_TEXT_CUSTOM_FIELD_FILTER_VALUE = 'PLAIN_TEXT_CUSTOM_FIELD_FILTER_VALUE';
export const PLAIN_TEXT_CUSTOM_FIELD_FILTER_VALUE_CHIP = 'PLAIN_TEXT_CUSTOM_FIELD_FILTER_VALUE_CHIP';
export const PLAIN_TEXT_CUSTOM_FIELD_FILTER_REMOVE_VALUE = 'PLAIN_TEXT_CUSTOM_FIELD_FILTER_REMOVE_VALUE';
export const PLAIN_TEXT_CUSTOM_FIELD_FILTER_ERROR = 'PLAIN_TEXT_CUSTOM_FIELD_FILTER_ERROR';

const OPERATOR_TEST_IDS: Record<PlainTextCustomFieldOperator, string> = {
  is: 'PLAIN_TEXT_CUSTOM_FIELD_FILTER_OPERATOR_IS',
  prefix: 'PLAIN_TEXT_CUSTOM_FIELD_FILTER_OPERATOR_PREFIX',
  or: 'PLAIN_TEXT_CUSTOM_FIELD_FILTER_OPERATOR_OR',
  not: 'PLAIN_TEXT_CUSTOM_FIELD_FILTER_OPERATOR_NOT',
  notSet: 'PLAIN_TEXT_CUSTOM_FIELD_FILTER_OPERATOR_NOT_SET',
};

export type PlainTextValueChipProps = {
  onRemoveValue: (value: string) => void;
  value: string;
};

const PlainTextValueChip = memo(({ onRemoveValue, value }: PlainTextValueChipProps) => {
  const onClick = useCallback(() => {
    onRemoveValue(value);
  }, [onRemoveValue, value]);

  return (
    <li>
      <Chip
        className="ads:bg-ads-gray-5 bg-gray-5 px-2 ads:text-secondary text-gray-11"
        data-testid={PLAIN_TEXT_CUSTOM_FIELD_FILTER_VALUE_CHIP}
        data-value={value}
        shape="rounded"
        size="medium"
      >
        <span className="truncate">{value}</span>
        <IconButton
          adsIcon
          appearance="ghost"
          color="gray"
          data-testid={PLAIN_TEXT_CUSTOM_FIELD_FILTER_REMOVE_VALUE}
          data-value={value}
          icon={XIcon}
          label={`Remove ${value}`}
          onClick={onClick}
          size="extra-small"
        />
      </Chip>
    </li>
  );
});

PlainTextValueChip.displayName = 'PlainTextValueChip';

export type PlainTextCustomFieldFilterProps = {
  fieldName: string;
  operator: PlainTextCustomFieldOperator;
  values: string[];
  draftValue: string;
  error: PlainTextCustomFieldValueError | null;
  onOperatorChange: (operator: PlainTextCustomFieldOperator) => void;
  onDraftValueChange: (value: string) => void;
  onCommitDraft: (options: { showEmptyError: boolean }) => void;
  onDraftBlur: (event: FocusEvent<HTMLInputElement>) => void;
  onRemoveValue: (value: string) => void;
};

export const PlainTextCustomFieldFilter = memo(
  ({
    fieldName,
    operator,
    values,
    draftValue,
    error,
    onOperatorChange,
    onDraftValueChange,
    onCommitDraft,
    onDraftBlur,
    onRemoveValue,
  }: PlainTextCustomFieldFilterProps) => {
    const showsValueInput = operator !== 'notSet';
    const showsValueChips = operator === 'or' || operator === 'not';
    const errorId = useId();
    const inputRef = useRef<HTMLInputElement>(null);
    const [removedChipCount, setRemovedChipCount] = useState(0);
    const handleRemoveValue = useCallback(
      (value: string) => {
        onRemoveValue(value);
        setRemovedChipCount((count) => count + 1);
      },
      [onRemoveValue],
    );

    useIsomorphicLayoutEffect(() => {
      if (removedChipCount === 0) {
        return;
      }

      inputRef.current?.focus();
    }, [removedChipCount]);
    const onValueChange = useCallback(
      (value: string) => {
        if (isPlainTextCustomFieldOperator(value)) {
          onOperatorChange(value);
        }
      },
      [onOperatorChange],
    );
    const onInputChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        onDraftValueChange(event.currentTarget.value);
      },
      [onDraftValueChange],
    );
    const onInputKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== 'Enter') {
          return;
        }
        event.preventDefault();
        onCommitDraft({ showEmptyError: true });
      },
      [onCommitDraft],
    );

    return (
      <div className="flex flex-col items-start gap-2">
        <Select onValueChange={onValueChange} value={operator}>
          <SelectTrigger
            aria-label={`${fieldName} operator`}
            className="w-auto border-0 font-medium"
            data-testid={PLAIN_TEXT_CUSTOM_FIELD_FILTER_OPERATOR}
            placeholder="Select operator"
            size="small"
          />
          <SelectContent className="min-w-[304px]" sideOffset={4}>
            {PLAIN_TEXT_CUSTOM_FIELD_OPERATORS.map((option) => (
              <SelectItem data-testid={OPERATOR_TEST_IDS[option]} key={option} value={option}>
                {PLAIN_TEXT_CUSTOM_FIELD_OPERATOR_LABELS[option]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {showsValueChips && values.length > 0 ? (
          <ul className="m-0 flex list-none flex-wrap gap-1 p-0">
            {values.map((value) => (
              <PlainTextValueChip key={value} onRemoveValue={handleRemoveValue} value={value} />
            ))}
          </ul>
        ) : null}
        {showsValueInput ? (
          <Input
            aria-describedby={error ? errorId : undefined}
            aria-invalid={error ? true : undefined}
            aria-label={`${fieldName} value`}
            className="w-full"
            data-testid={PLAIN_TEXT_CUSTOM_FIELD_FILTER_VALUE}
            ref={inputRef}
            hasError={!!error}
            onBlur={onDraftBlur}
            onChange={onInputChange}
            onKeyDown={onInputKeyDown}
            placeholder={showsValueChips ? 'Add a value' : 'Value'}
            size="medium"
            value={draftValue}
          />
        ) : null}
        {error ? (
          <p
            className="m-0 ads:text-error text-12 text-red-9"
            data-testid={PLAIN_TEXT_CUSTOM_FIELD_FILTER_ERROR}
            id={errorId}
            role="alert"
          >
            {plainTextCustomFieldValueErrorMessage(error)}
          </p>
        ) : null}
      </div>
    );
  },
);

PlainTextCustomFieldFilter.displayName = 'PlainTextCustomFieldFilter';
